import { useCallback } from 'react';
import { showAlert } from '@silverfort/sf-components';
import { LocalAccount } from '../../../types/LocalAccount';
import { BindLocalAccountPopupState } from '../types';
import { usePopup } from '../../../context/PopupProvider';
import { BIND_POPUP_CONFIG } from '../popupConfigs';
import { useBindLocalAccounts, useBindLocalAccount, BindLocalAccountResponse } from '../../../api';
import { Entity } from '../../../../../api';

interface UseBindLocalAccountsPopupParams {
    accounts: LocalAccount[];
    onAccountsBound?: () => void;
}

interface UseBindLocalAccountPopupParams {
    account: LocalAccount;
    onAccountBound?: (adEntityGuid: Entity['guid']) => void;
}

interface UseBindLocalAccountsPopupReturnType {
    openBindLocalAccountsPopup: () => void;
}

export const useBindLocalAccountPopup = ({
    account,
    onAccountBound
}: UseBindLocalAccountPopupParams): UseBindLocalAccountsPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const handleSuccess = useCallback(
        (response: BindLocalAccountResponse) => {
            closePopup();
            const alertMessage = response.limitExceeded
                ? `Only ${response.boundUsersCount} members from the specified group or OU will be bound to the local account.`
                : 'Local account successfully bound.';
            showAlert(alertMessage, response.limitExceeded ? 'warning' : 'success', { title: 'Bind successful' });
            onAccountBound?.(response.adEntityGuid);
        },
        [closePopup, onAccountBound]
    );

    const handleError = useCallback(() => {
        closePopup();
        showAlert('Failed to bind local account.', 'error', { title: 'Bind failed' });
    }, [closePopup]);

    const { bindLocalAccount } = useBindLocalAccount({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const openBindLocalAccountsPopup = useCallback(() => {
        const stateConfig = account.boundAdEntity
            ? { initialState: { boundEntity: account.boundAdEntity } }
            : { title: 'Bind AD accounts', initialState: {} };

        const config = {
            ...BIND_POPUP_CONFIG,
            onApprove: async ({ boundEntity }: BindLocalAccountPopupState = {}) => {
                if (boundEntity) {
                    await bindLocalAccount(account.guid, boundEntity);
                }
            },
            ...stateConfig
        };

        openPopup(config);
    }, [account, bindLocalAccount, openPopup]);

    return { openBindLocalAccountsPopup };
};

export const useBindLocalAccountsPopup = ({
    accounts,
    onAccountsBound
}: UseBindLocalAccountsPopupParams): UseBindLocalAccountsPopupReturnType => {
    const { openPopup, closePopup } = usePopup();

    const handleSuccess = useCallback(
        (response: BindLocalAccountResponse) => {
            closePopup();
            const alertMessage = response.limitExceeded
                ? `Only ${response.boundUsersCount} members from the specified group or OU will be bound to the local accounts.`
                : 'Local accounts bound successfully.';
            showAlert(alertMessage, response.limitExceeded ? 'warning' : 'success', { title: 'Bind successful' });
            onAccountsBound?.();
        },
        [closePopup, onAccountsBound]
    );

    const handleError = useCallback(() => {
        closePopup();
        showAlert('Failed to bind local accounts', 'error', { title: 'Bind failed' });
    }, [closePopup]);

    const { bindLocalAccounts } = useBindLocalAccounts({
        onSuccess: handleSuccess,
        onError: handleError
    });

    const openBindLocalAccountsPopup = useCallback(() => {
        const config = {
            ...BIND_POPUP_CONFIG,
            onApprove: async ({ boundEntity }: BindLocalAccountPopupState = {}) => {
                if (boundEntity) {
                    await bindLocalAccounts(
                        accounts.map((a) => a.guid),
                        boundEntity
                    );
                }
            },
            initialState: {}
        };

        openPopup(config);
    }, [accounts, bindLocalAccounts, openPopup]);

    return { openBindLocalAccountsPopup };
};
